import './Post.sass';

import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRoute, useRouter } from 'react-router5';
import { helmetJsonLdProp } from 'react-schemaorg';
import { Article } from 'schema-dts';

import { Button, HorizontalScroll, LottieIcon } from 'uikit';
import {
  IcAdd28,
  IcAngleRight16,
  IcDollar24,
  IcLike24,
  IcMessage28,
  IcShare24,
  IcViews24,
} from 'assets';
import {
  useCurrentLang,
  useCurrentUserInfo,
  useDecryptKey,
  useGroupInfo,
  useParseText,
  usePersonSchema,
  usePost,
  useScheduled,
  useTranslator,
  useUnitInfo,
  useUnitName,
  useUserInfo,
  useVisibleDuration,
} from 'hooks';
import { postsActions } from 'store/posts';
import {
  Attachments,
  MetaTags,
  Poll,
  Roulette,
  ShareSheet,
  Truncate,
  UnitLink,
  UnitName,
} from '$shared/components';
import {
  CHAT,
  COMMON_FEED,
  PROFILE,
  RESOLVE,
  SUBSCRIPTION_ADULT,
  SUBSCRIPTION_AUTHOR,
  SUBSCRIPTION_BASIC,
  SUBSCRIPTION_MEDIA,
} from 'shared/constants/pages';
import { IcComment24 } from '$assets';
import { showModal } from '$navigation/helper';
import {
  AttachLink,
  AttachType,
  AuthPopoverState,
  CurrencyEnum,
  PostType,
} from '$store/models';
import { followersActions, followersSelector } from '$store/followers';
import { mainActions, mainSelector } from '$store/main';
import {
  classNames,
  cutNumberFractionalPart,
  formatBigNumber,
  getDistanceBetweenTwoElements,
  isSponsorAbTest,
  setStorageItem,
  statReachGoal,
  transliterate,
} from '$utils';
import { authSelector } from '$store/auth';
import { PostProps } from './Post.interface';
import { profileActions } from '$store/profile';
import { imActions } from '$store/im';
import { balanceActions } from '$store/balance';
import { PostHeader } from '$shared/components/Post/PostHeader/PostHeader';

const PostComponent: FC<PostProps> = (props) => {
  const {
    postId,
    isFullPage = false,
    showFollowBtn = false,
    isRecommendations = false,
    isReport: isRepost = false,
    isAds = false,
    isFeed = false,
    sourceLabel,
  } = props;

  const dispatch = useDispatch();
  const router = useRouter();
  const { route } = useRoute();
  const { isLogged } = useSelector(authSelector);
  const post = usePost(postId);
  const currentUser = useCurrentUserInfo();
  const user = useUserInfo(post.publisherId);
  const group = useGroupInfo(-post.ownerId);
  const unitName = useUnitName(post.ownerId < 0 ? group : user);
  const currentLang = useCurrentLang();

  const text = useMemo(() => {
    if (!post.translations) {
      return post.text;
    }

    let translations = post.translations;
    if (typeof translations === 'string') {
      try {
        translations = JSON.parse(translations);
      } catch (e) {
        return post.text;
      }
    }

    const translation = translations[currentLang.key];
    if (!translation) {
      return post.text;
    }

    if (translation.startsWith('{') && translation.endsWith('}')) {
      return translation;
    }
    if (translation.startsWith('"') && translation.endsWith('"')) {
      try {
        return JSON.parse(translation);
      } catch (e) {}
    }
    return translation;
  }, [post, currentLang]);

  const postText = useParseText(text);
  const [isShareButtonHidden, setIsShareButtonHidden] = useState(false);
  const [isTipsTextHidden, setIsTipsTextHidden] = useState(false);
  const postRef = useRef<HTMLDivElement>(null);
  const footerActionsRef = useRef<HTMLDivElement>(null);
  const postViewsRef = useRef<HTMLDivElement>(null);
  const { following } = useSelector(followersSelector);
  const t = useTranslator();
  const decryptKey = useDecryptKey(post);
  const isProfilePage = route.name === PROFILE || route.name === RESOLVE;
  const isProfilePost = isProfilePage || route.name === COMMON_FEED;
  const [isInstantMessagesBlockShown, setInstantMessagesBlockShown] = useState(false);
  const { key } = useCurrentLang();
  const parentOwner = useUnitInfo(post.parentOwnerId ?? 0);
  const { decryptKeys, isModer } = useSelector(mainSelector);

  const isVisible = useVisibleDuration(postRef, (postViewDuration) => {
    (canView || !!post.adId) &&
      dispatch(
        postsActions.viewPost({
          postId,
          postViewDuration,
        }),
      );

    if (post.isAdult) {
      statReachGoal('view_post', {
        duration: postViewDuration,
      });
    }
  });

  const poll = useMemo(() => {
    if (!post.attachments) {
      return null;
    }

    for (let item of post.attachments) {
      if (item.type === 'poll') {
        return item.poll;
      }
    }

    return null;
  }, [post.attachments]);

  const roulette = useMemo(() => {
    return (
      post.attachments?.find((item) => item.type === AttachType.ROULETTE)?.roulette ||
      null
    );
  }, [post.attachments]);

  const isFollowed = following[post.ownerId];
  const canView =
    isModer ||
    !!decryptKey ||
    post.type === PostType.FREE ||
    (post.type === PostType.SUBSCRIPTION && !post.isAdult);

  useEffect(() => {
    window.addEventListener('resize', getIsFooterElementsOverlap);

    return () => {
      window.removeEventListener('resize', getIsFooterElementsOverlap);
    };
  }, [
    postViewsRef.current,
    footerActionsRef.current,
    isShareButtonHidden,
    isTipsTextHidden,
  ]);

  useEffect(() => {
    if (isVisible && postRef.current && canView) {
      if (isAds) {
        statReachGoal('view_ad');
      }
    }
  }, [isVisible]);

  const [isLikedAnimation, setIsLikedAnimation] = useState(false);
  const handleLike = useCallback(() => {
    if (isLogged) {
      setIsLikedAnimation(true);
      dispatch(postsActions.toggleLike(postId));
    } else {
      handleAuthPopoverShown();
    }
  }, [isLogged]);

  useEffect(() => {
    getIsFooterElementsOverlap();
  }, [
    postViewsRef.current,
    footerActionsRef.current,
    isShareButtonHidden,
    isTipsTextHidden,
  ]);

  const isScheduled = useScheduled(post.createdAt);
  const personSchema = usePersonSchema(user);

  const articleJsonLd = useMemo(() => {
    const imageUrl = post.attachments?.filter((a) => a.type === AttachType.PHOTO)[0]
      ?.photo?.photoLarge;

    return helmetJsonLdProp<Article>(
      user
        ? {
            '@context': 'https://schema.org',
            '@type': 'Article',
            '@id': 'post' + post.id,
            articleSection:
              post.type === PostType.SUBSCRIPTION
                ? user?.verified === 2
                  ? 'Erotic'
                  : 'Media'
                : 'Sponsors',
            author: personSchema,
            articleBody: postText,
            image: imageUrl,
            url: `https://${location.hostname}/${user.domain || user.id}?w=post${
              post.id
            }`,
          }
        : {
            '@context': 'https://schema.org',
            '@type': 'Article',
          },
    );
  }, [post, user, postText]);

  const hiddenPostActions = useMemo(() => {
    const type = post.type;
    let text = t('post_hidden_subscription');
    let action;

    setStorageItem('targetId', `${post.ownerId}`);

    if (isLogged) {
      if (type === PostType.FREE && post.isAdult) {
        action = () => router.navigate(SUBSCRIPTION_BASIC);
        text = t('post_hidden_subscription_basic');
      } else if (
        type === PostType.PRIVATE_SUBSCRIPTION ||
        (post.isAdult && !decryptKeys[post.ownerId] && !isSponsorAbTest(currentUser.id))
      ) {
        action = () => router.navigate(SUBSCRIPTION_AUTHOR, { authorId: post.ownerId });
        text = t('post_hidden_subscription_author');
      } else if (post.ownerId < 0 || user?.verified !== 2) {
        action = () => router.navigate(SUBSCRIPTION_MEDIA);
      } else {
        action = () => {
          router.navigate(SUBSCRIPTION_ADULT, {
            target: 'post',
            target_model: post.ownerId,
          });
          statReachGoal('show_paywall_from_hidden_post');
        };
      }
    } else {
      action = handleAuthPopoverShown;
    }

    return { action, text };
  }, [
    post.type,
    post.ownerId,
    user?.verified,
    isLogged,
    decryptKeys,
    currentUser,
    post.isAdult,
  ]);

  const handleFollow = useCallback(() => {
    if (post.adId > 0) {
      dispatch(
        followersActions.followFromAds({
          toId: post.ownerId,
          adsId: post.adId,
        }),
      );
    } else {
      dispatch(followersActions.follow(post.ownerId));
      statReachGoal('follow_from_post');
    }
  }, [post]);

  const handleOpenChat = useCallback(() => {
    router.navigate(CHAT, {
      peerId: post.ownerId,
    });
    statReachGoal('show_chat_from_post');
  }, [post]);

  const handleClick = useCallback(
    (e) => {
      const target = e.target;
      e.stopPropagation();

      if (target.closest('.Poll') || target.closest('.Roulette')) {
        return;
      }

      if (target.closest('.Collage__item')) {
        const el = target.closest('.Collage__item');
        const id = el.dataset.id * (el.classList.contains('video') ? -1 : 1);
        showModal(`photos+post${postId}+${id}`);
        return;
      }

      if (target.closest('.Post__repost')) {
        e.stopPropagation();
      }

      if (target.closest('.Post__likes')) {
        if (!isLogged) {
          dispatch(profileActions.setAuthPopoverState(AuthPopoverState.LOGIN));
          return;
        }
        showModal(`likes_post${postId}`);
        return;
      }

      if (target.closest('.Post__header__follow')) {
        if (!isLogged) {
          dispatch(profileActions.setAuthPopoverState(AuthPopoverState.LOGIN));
          return;
        }
        if (post.isAdult) {
          if (isFollowed) {
            handleOpenChat();
          } else {
            handleFollow();
          }
        } else {
          if (isFollowed) {
            dispatch(followersActions.unfollow(post.ownerId));
          } else {
            handleFollow();
          }
        }
        return;
      }

      if (
        isFullPage ||
        target.closest('.ExternalVideo') ||
        target.closest('.InlineVideo') ||
        target.closest('a') ||
        target.closest('.link') ||
        target.closest('.Button') ||
        target.closest('.__prevent') ||
        target.closest('.ActionSheet')
      ) {
        return;
      }

      if (!isAds) {
        if ('getSelection' in window) {
          const selection = window.getSelection();
          if (
            selection &&
            selection.focusNode &&
            selection.focusNode.parentElement &&
            selection.focusNode.parentElement.closest('.Post')
          ) {
            const selectionLen = Math.abs(selection.focusOffset - selection.anchorOffset);
            if (selectionLen > 1) {
              return;
            }
          }
        }

        // if (post.parentId > 0) {
        //   showModal(`post${post.parentId}`);
        // } else {
        //   showModal(`post${post.id}`);
        // }
      }
    },
    [
      dispatch,
      isFullPage,
      post.id,
      post.ownerId,
      isFollowed,
      isLogged,
      handleFollow,
      handleOpenChat,
    ],
  );

  const handleComment = useCallback(() => {
    if (isLogged) {
      showModal(`post${post.id}`);
    } else {
      handleAuthPopoverShown();
    }
  }, [post.id, isLogged]);

  const handleRestore = useCallback(() => {
    dispatch(postsActions.restorePost(postId));
  }, [dispatch, postId]);

  const showInstantMessagesBlock = useMemo(() => {
    return false;
    // if (isInstantMessagesBlockShown || !isFeed || post.adId > 0) {
    //   return false;
    // }
    //
    // if (!post || post.ownerId < 0 || !post.isAdult) {
    //   return false;
    // }
    //
    // if (user.verified !== 2 || Date.now() / 1000 - user.lastUpdate > 10 * 60) {
    //   return false;
    // }
    //
    // const lastShow = localStorage.getItem(`instantMessageSent_${post.ownerId}`) || 0;
    // return Date.now() - +lastShow > 86400 * 1000;
  }, [post, isInstantMessagesBlockShown, isFeed]);

  const handleSendMessage = useCallback(
    (text: string) => () => {
      dispatch(
        imActions.sendInstantMessage({
          peerId: post.ownerId,
          text,
        }),
      );
      setInstantMessagesBlockShown(true);
    },
    [post],
  );

  const handleRepost = () => {
    if (!isLogged) {
      handleAuthPopoverShown();
      return;
    }

    const link: AttachLink = {
      type: 'post',
      object: post,
    };

    if (post.ownerId > 0) {
      link.extra = user;
    } else {
      link.extra = group;
    }

    dispatch(
      mainActions.setPopup(<ShareSheet title={t('post_share_title')} link={link} />),
    );
  };

  const handleTips = () => {
    if (!isLogged) {
      handleAuthPopoverShown();
      return;
    }

    dispatch(
      balanceActions.setTipsData({
        peerId: post.ownerId,
        postId,
        currency: user?.verified > 0 ? CurrencyEnum.EUR : CurrencyEnum.TON,
      }),
    );
  };

  const handlePublish = useCallback(() => {
    dispatch(postsActions.publishScheduled(postId));
  }, [postId, dispatch]);

  const textOptions = useMemo(() => {
    if (post.attachments && post.attachments.length) {
      let hasMedia = false;
      for (let attach of post.attachments) {
        if (attach.type === 'photo' || attach.type === 'video') {
          hasMedia = true;
          break;
        }
      }

      if (hasMedia) {
        return {
          lines: 6,
          limit: 180,
          shortFrom: 126,
        };
      }
    }

    return {
      lines: 12,
      limit: 300,
      shortFrom: 250,
    };
  }, [post.attachments]);

  if (post.isDeleted) {
    return (
      <div className="Post Post__deleted">
        <div className="Post__deleted__label">{t('post_deleted')}</div>
        <div className="Post__deleted__actions">
          <Button mode="secondary" size="small" onClick={handleRestore}>
            {t('post_restore')}
          </Button>
        </div>
      </div>
    );
  }

  function getIsFooterElementsOverlap() {
    const footerActionsElement = footerActionsRef.current;
    const postViewsElement = postViewsRef.current;

    if (!footerActionsElement || !postViewsElement) {
      return;
    }

    const distance = getDistanceBetweenTwoElements(
      footerActionsElement,
      postViewsElement,
    );

    if (isShareButtonHidden) {
      if (isTipsTextHidden && distance <= 100) {
        return;
      }

      setIsTipsTextHidden(distance <= 5);
    }

    if (isShareButtonHidden && distance <= 115) {
      return;
    }

    setIsShareButtonHidden(distance <= 5);
  }

  function handleAuthPopoverShown() {
    dispatch(
      profileActions.setAuthPopoverState(isProfilePost ? AuthPopoverState.LOGIN : null),
    );
  }

  function renderAdsActions() {
    if (isAds && (!isFollowed || post.isAdult)) {
      return (
        <div className="Post__ads_actions">
          {isFollowed ? (
            <Button
              stretched
              mode="quinary"
              onClick={() => router.navigate(CHAT, { peerId: post.ownerId })}
            >
              {t('post_send_message')}
            </Button>
          ) : (
            <Button stretched onClick={handleFollow} flare icon={<IcAdd28 />}>
              {t('ads_follow_button')}
            </Button>
          )}
        </div>
      );
    }

    return null;
  }

  function renderFooter() {
    if (isAds && !isFollowed) {
      return (
        <div className="Ads__footer" onClick={handleFollow}>
          {t('post_ads_footer')} <span>{t('post_ads_footer_btn')}</span>.
        </div>
      );
    }

    if (!isScheduled) {
      return (
        <>
          <div className="Post__footer__actions" ref={footerActionsRef}>
            <Button
              size="small"
              mode={post.isLiked ? 'destructive' : 'secondary'}
              onClick={handleLike}
              icon={
                post.isLiked ? (
                  <LottieIcon
                    size={60}
                    name="post_like"
                    autoplay={isLikedAnimation}
                    onComplete={() => setIsLikedAnimation(false)}
                    initialSegment={
                      post.isLiked && !isLikedAnimation ? [180, 180] : undefined
                    }
                  />
                ) : (
                  <IcLike24 />
                )
              }
            >
              {!post.isAdult ||
              (!!currentUser && currentUser.id === post.ownerId) ||
              !post.likes
                ? null
                : formatBigNumber(post.likes)}
            </Button>
            {/*{!isFullPage && (*/}
            {/*  <Button*/}
            {/*    size="small"*/}
            {/*    mode="secondary"*/}
            {/*    icon={<IcComment24 />}*/}
            {/*    onClick={handleComment}*/}
            {/*  >*/}
            {/*    {post.comments > 0 ? formatBigNumber(post.comments) : null}*/}
            {/*  </Button>*/}
            {/*)}*/}
            {(isFullPage || !isShareButtonHidden) && (
              <Button
                size="small"
                mode="secondary"
                icon={<IcShare24 />}
                onClick={handleRepost}
              >
                {post.shares > 0 ? formatBigNumber(post.shares) : null}
              </Button>
            )}
            {post.ownerId > 0 && (
              <Button
                size="small"
                mode="secondary"
                icon={<IcDollar24 />}
                onClick={handleTips}
              >
                {post.tips > 0
                  ? formatBigNumber(+cutNumberFractionalPart(post.tips))
                  : !isTipsTextHidden && t('send_tip_title')}
              </Button>
            )}
          </div>
          {post.views > 0 ? (
            <div className="Post__views" ref={postViewsRef}>
              <div className="Post__views__icon">
                <IcViews24 />
              </div>
              {formatBigNumber(post.views)}
            </div>
          ) : null}
        </>
      );
    } else {
      return (
        <div className="Post__publish_now_button">
          <Button size="small" onClick={handlePublish}>
            {t('post_scheduled_publish_now')}
          </Button>
        </div>
      );
    }
  }

  // @ts-ignore
  return (
    <article
      className={classNames('Post', {
        _isHidden: !canView,
        _isSinglePhotoAttachment:
          post.attachments &&
          post.attachments.length === 1 &&
          post.attachments[0].type === 'photo',
      })}
      onClick={handleClick}
      ref={postRef}
    >
      <div className="Post__roulette_bg"></div>
      {isFullPage && (
        <MetaTags
          title={`${unitName}: ${postText.substring(0, 30)}`}
          description={postText.substring(0, 300)}
          script={[articleJsonLd]}
        />
      )}
      <PostHeader
        postId={postId}
        unitName={unitName}
        showFollowBtn={showFollowBtn}
        isFeed={isFeed}
        sourceLabel={sourceLabel}
        isFollowed={isFollowed}
        isRecommendations={isRecommendations}
        isScheduled={isScheduled}
        isFullPage={isFullPage}
        isShareButtonHidden={isShareButtonHidden}
        handleRepost={handleRepost}
        isVisible={isVisible}
      />
      {showInstantMessagesBlock && (
        <div className="Post__instant_messages">
          <div className="Post__instant_messages__title">
            {t('post_im_caption', {
              name: key === 'ru' ? user.firstName : transliterate(user.firstName),
            })}
          </div>
          <div className="Post__instant_messages__templates">
            <HorizontalScroll>
              <Button size="small" icon={<IcMessage28 />} onClick={handleOpenChat} />
              {[
                t('post_im_message1'),
                '🍆',
                t('post_im_message2'),
                t('post_im_message3'),
                t('post_im_message4'),
                t('post_im_message5'),
                t('post_im_message6'),
                t('post_im_message7'),
              ].map((item, i) => (
                <Button size="small" key={i} onClick={handleSendMessage(item)}>
                  {item}
                </Button>
              ))}
            </HorizontalScroll>
          </div>
        </div>
      )}
      <div
        onClick={(e) => {
          if (!canView) {
            e.stopPropagation();
            hiddenPostActions.action();
          }
        }}
      >
        {!isFullPage && post.parentOwnerId && post.parentOwnerId > 0 && parentOwner && (
          <div className="Post__answer">
            {t('view_post_parent_button')}{' '}
            <UnitLink unit={parentOwner}>
              <UnitName unit={parentOwner} />
            </UnitLink>
          </div>
        )}
        <Attachments
          attachments={post.attachments}
          parentObject={`post_${postId}`}
          decryptKey={decryptKey}
          canExpand={!isRepost}
          isBlocked={!canView}
          isVisible={isVisible}
          adId={post.adId}
        />
      </div>
      {!canView && (
        <Button
          onClick={hiddenPostActions.action}
          iconRight={<IcAngleRight16 />}
          className="Post__hidden__actions_button"
          size="medium"
          fillColor={post.type === PostType.PRIVATE_SUBSCRIPTION ? 'red' : undefined}
        >
          {hiddenPostActions.text}
        </Button>
      )}
      {post.text.length > 0 && (
        <div
          className={classNames({
            Post__text: true,
            short: post.text.length <= textOptions.shortFrom,
          })}
        >
          <Truncate
            lines={textOptions.lines}
            cut={!canView}
            limit={isFullPage ? 0 : textOptions.limit}
            text={text}
            expandText={t('post_text_expand')}
            adId={post.adId}
          />
        </div>
      )}
      {poll && <Poll poll={poll} postId={postId} />}
      {roulette && (
        <Roulette
          canRotate={currentUser.id !== post.ownerId}
          roulette={roulette}
          postId={postId}
        />
      )}
      {renderAdsActions()}
      {!(!!currentUser && post.isAdult && post.ownerId !== currentUser.id) &&
        !isAds &&
        post.likes > 0 && (
          <div className="Post__likes">{`${formatBigNumber(post.likes)} ${t(
            'likes_count',
            { count: post.likes },
          )}`}</div>
        )}
      <div className="Post__footer">{renderFooter()}</div>
    </article>
  );
};

export const Post = React.memo(PostComponent);
